import { Check } from 'lucide-react';

import { Button } from '~/components/ui/button';
import type {
  ProductSearchImpact,
  ReviewSeverity,
} from '~/features/catalog-operations/types/search-impact.types';
import { cn } from '~/utils/cn';

interface Props {
  review: ProductSearchImpact['review'];
  cleared: ProductSearchImpact['cleared'];
  /** Persists a dismissal (or its undo) — recorded server-side, so it survives recomputes. */
  onSetDismissed: (itemKey: string, dismissed: boolean) => void;
  /** Item currently being saved, so its button shows a spinner. */
  pendingItemKey: string | null;
  /** Jumps to the source data, so the reviewer can check the claim. */
  onOpenSourceData: () => void;
}

const SEVERITY_STYLE: Record<ReviewSeverity, { badge: string; item: string }> = {
  advisory: { badge: 'bg-warning-50 text-warning', item: 'border-warning/30 bg-warning-50/40' },
  critical: { badge: 'bg-error-50 text-error', item: 'border-error/30 bg-error-50/40' },
};

/**
 * Items a person should look at before publishing (most volume at risk first, as
 * the server orders them), plus the checks that passed — so "nothing flagged" is
 * visible, not assumed. Dismissed items stay listed, quieter, with Undo.
 */
export function NeedsReviewPanel({
  review,
  cleared,
  onSetDismissed,
  pendingItemKey,
  onOpenSourceData,
}: Props) {
  return (
    <section
      className="rounded-2xl border bg-white p-6 shadow-sm"
      data-testid="search-impact-review-card"
    >
      <h2 className="text-base font-semibold" data-testid="search-impact-review-title">
        Needs review
      </h2>
      <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
        Anything a person should look at before this product publishes.
      </p>

      {review.length > 0 && (
        <div className="mt-4 flex flex-col gap-3" data-testid="search-impact-review-list">
          {review.map((item) => (
            <div
              key={item.itemKey}
              data-testid={`search-impact-review-item-${item.itemKey}`}
              className={cn(
                'flex items-start gap-3.5 rounded-xl border p-4',
                item.dismissed ? 'bg-muted/30 opacity-70' : SEVERITY_STYLE[item.severity].item,
              )}
            >
              <span
                data-testid={`search-impact-review-item-${item.itemKey}-severity-badge`}
                className={cn(
                  'flex-none rounded-md px-2 py-1 text-[11px] font-semibold tracking-wider uppercase',
                  item.dismissed
                    ? 'bg-muted text-muted-foreground'
                    : SEVERITY_STYLE[item.severity].badge,
                )}
              >
                {item.dismissed ? 'Dismissed' : item.severity}
              </span>
              <div className="min-w-0 flex-1">
                <h3
                  className="text-sm font-semibold"
                  data-testid={`search-impact-review-item-${item.itemKey}-title`}
                >
                  {item.title}
                </h3>
                <p className="text-muted-foreground mt-1 text-sm leading-relaxed">{item.body}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={item.dismissed ? 'outline' : 'default'}
                    isLoading={pendingItemKey === item.itemKey}
                    data-testid={`search-impact-review-item-${item.itemKey}-dismiss-button`}
                    onClick={() => {
                      onSetDismissed(item.itemKey, !item.dismissed);
                    }}
                    className="cursor-pointer"
                  >
                    {item.dismissed ? 'Undo dismiss' : 'Dismiss'}
                  </Button>
                  {!item.dismissed && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={onOpenSourceData}
                      className="cursor-pointer"
                      data-testid={`search-impact-review-item-${item.itemKey}-open-source-button`}
                    >
                      Open source data
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {cleared.length > 0 && (
        <ul
          className="mt-4 overflow-hidden rounded-xl border"
          data-testid="search-impact-cleared-list"
        >
          {cleared.map((check) => (
            <li
              key={check.label}
              data-testid={`search-impact-cleared-item-${check.label}`}
              className="flex items-center gap-3 border-t px-4 py-3 text-sm font-semibold first:border-t-0"
            >
              <span className="bg-success-50 text-success grid h-5 w-5 flex-none place-items-center rounded-full">
                <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
              </span>
              <span className="flex-1">{check.label}</span>
              <span
                className="text-muted-foreground text-xs font-semibold"
                data-testid={`search-impact-cleared-item-${check.label}-value`}
              >
                {check.result}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
